import { camelCase, startCase } from 'lodash';
import { ITransition } from './transition';
import { ISelector } from './utils';
import UUIDHelper from './uuid';

export interface IStreamResolution {
  label: string;
  value: string;
}
export interface IProject {
  id: number;
  uuid?: string;
  title: string;
  settings: any;
  scenes: IScene[];
  active: boolean;
  active_scene_id: number;
  active_scene?: IScene;
  scene_transition_id?: number;
  scene_transition?: ITransition;
}

export interface IScene {
  id?: number;
  uuid?: string;
  project_id?: number;
  settings: any;
  components: IComponent[];
  title: string;
  theme_id?: number;
}

export interface IComponent {
  id: string;
  type: TComponentType;
  name: string;
  css: IComponentCss;
  x: number;
  y: number;
  w: number;
  h: number;
  value?: string;
  metadata?: any;
  events?: any;
  customHtml?: string;
  customCss?: string;
  customJs?: string;
  locked?: boolean;
  hidden?: boolean;
  newlyAdded?: boolean;
  lockAspectRatio?: boolean;
  aspectRatio?: number;
  isFullWidth?: boolean;
  lockHorizontal?: boolean;
  useSLSettings?: boolean;
  fixedPosition?: boolean;
  sortIndex?: number;
  hideable?: boolean;
  useFullWidth?: boolean;
  useFullHeight?: boolean;
  verticalSettings?: Partial<IComponent>;
}

export interface IVideoComponent extends IComponent {
  metadata: IVideoComponentMetadata;
}

export interface IAlertBoxComponent extends IComponent {
  events: IAlertBoxAlertEvent;
}

export interface ISLWidgetComponent extends IComponent {
}

export interface IGameCaptureComponent extends IComponent {
}
export interface ICustomComponent extends IComponent {
  customHtml: string;
  customCss: string;
  customJs: string;
}

export interface IWebcamComponent extends IComponent {
  preview: boolean;
  facingMode: 'user' | 'environment',
  deviceId?: string;
  videoDeviceId: string;
  audioDeviceId: string;
  delay?: number;
  volume?: number;
  muted?: boolean;
}

export interface IImageComponent extends IComponent {
  isBorder?: boolean;
}

export interface IStreamLabelComponent extends IComponent {
}

export interface IComponentCss {
  width?: number | string;
  height?: number | string;
  top?: number | string;
  left?: number | string;
  zIndex?: number;
  fontSize?: string | number;
  color?: string;
  opacity?: number;
  lineHeight?: string;
  letterSpacing?: string;
  fontFamily?: string;
  fontWeight?: number;
  fontStyle?: TFontStyle;
  textAlign?: TTextAlign;
  textDecoration?: string;
  textShadowEnabled?: boolean;
  textStrokeEnabled?: boolean;
  tickerEnabled?: boolean;
  backgroundColor?: string;
  textShadow?: string;
  textShadowOffset?: string;
  textShadowColor?: string;
  textStrokeColor?: string;
  webkitTextStrokeColor?: string;
  textStrokeWidth?: number;
  webkitTextStrokeWidth?: string;
  rotate?: number;
  transform?: string;
  border?: string;
  borderEnabled?: boolean;
  borderWidth?: number;
  borderRadius?: number | string;
  borderColor?: string;
}

export type TFontStyle = 'normal' | 'italic';
export type TTextAlign = 'left' | 'center' | 'right';

export interface IVideoComponentMetadata {
  volume: number;
  muted: boolean;
  loop: boolean;
}

export interface IAlertBoxAlertEvent {
  follows: boolean;
  subscriptions: boolean;
  resubs: boolean;
  donations: boolean;
  hosts: boolean;
  bits: boolean;
  raids: boolean;
  redemptions: boolean;
  merch: boolean;
  cloudbotRedemptions: boolean;
  streamlabsPrimeGifts: boolean;
}

export type TComponentType = 'text' | 'image' | 'video' | 'alertBox' | 'customComponent' | 'webcam' | 'chat' | 'eventList' | 'streamLabel' | 'gameCapture';

export enum EComponentType {
  text = 'text',
  image = 'image',
  video = 'video',
  alertBox = 'alertBox',
  customComponent = 'customComponent',
  webcam = 'webcam',
  chat = 'chat',
  eventList = 'eventList',
  streamLabel = 'streamLabel',
  gameCapture = 'gameCapture',
}

export type TSettingType = 'settings' | 'template';

export enum ESettingType {
  settings = 'settings',
  template = 'template',
}

export default class ComponentHelper {
  static get isUniqueComponents(): TComponentType[] {
    return [EComponentType.alertBox, EComponentType.eventList, EComponentType.chat, EComponentType.webcam, EComponentType.gameCapture];
  }

  static get isSLComponents(): TComponentType[] {
    return [EComponentType.alertBox, EComponentType.eventList, EComponentType.chat, EComponentType.streamLabel];
  }

  static get defaultTextSettings(): IComponent {
    return {
      id: UUIDHelper.create(),
      type: EComponentType.text,
      name: 'Text',
      css: {
        fontSize: 32,
        color: '#ffffff',
      },
      value: 'Your text goes here',
      h: 48,
      w: 300,
      y: 0,
      x: 0,
    };
  }

  static get defaultImageSettings(): IImageComponent {
    return {
      id: UUIDHelper.create(),
      type: EComponentType.image,
      name: 'Image',
      css: {},
      value:
        'https://cdn.streamlabsstudio.com/static/logos/streamlabs-console.png',
      h: 40,
      w: 250,
      y: 0,
      x: 0,
      lockAspectRatio: true,
      aspectRatio: 6.25,
      isBorder: false,
    };
  }

  static get defaultVideoSettings(): IVideoComponent {
    return {
      id: UUIDHelper.create(),
      type: EComponentType.video,
      name: 'Video',
      css: {},
      value:
        'https://cdn.streamlabs.com/static/overlay-editor/overlay-editor__video-component-placeholder.webm',
      h: 180,
      w: 320,
      y: 0,
      x: 0,
      metadata: {
        volume: 100,
        muted: true,
        loop: true,
      },
    };
  }
  static get defaultAlertBoxSettings(): IAlertBoxComponent {
    return {
      id: UUIDHelper.create(),
      type: EComponentType.alertBox,
      name: 'Alert Box',
      css: {},
      h: 600,
      w: 800,
      x: 0,
      y: 0,
      useSLSettings: false,
      lockAspectRatio: false,
      aspectRatio: 800 / 600,
      events: {
        follows: true,
        subscriptions: true,
        resubs: true,
        donations: true,
        hosts: true,
        bits: true,
        raids: true,
        redemptions: true,
        merch: true,
        cloudbotRedemptions: true,
        streamlabsPrimeGifts: true,
      },
      customHtml: '',
      customCss: '',
      customJs: '',
      verticalSettings: {
        h: 350,
        w: 500,
        x: 110,
        y: 565,
      },
    };
  }

  static get defaultChatSettings(): ISLWidgetComponent {
    return {
      id: UUIDHelper.create(),
      type: EComponentType.chat,
      name: 'Chat',
      css: {},
      h: 260,
      w: 600,
      x: 20,
      y: 440,
      useSLSettings: false,
      lockAspectRatio: false,
      customHtml: '',
      customCss: '',
      customJs: '',
      verticalSettings: {
        h: 230,
        w: 720,
        x: 0,
        y: 940,
        useFullWidth: true,
      },
    };
  }

  static get defaultEventListSettings(): ISLWidgetComponent {
    return {
      id: UUIDHelper.create(),
      type: EComponentType.eventList,
      name: 'Event List',
      css: {},
      h: 260,
      w: 600,
      x: 0,
      y: 0,
      useSLSettings: false,
      lockAspectRatio: false,
      customHtml: '',
      customCss: '',
      customJs: '',
      verticalSettings: {
        h: 230,
        w: 720,
        x: 0,
        y: 610,
        useFullWidth: true,
      },
    };
  }

  static get defaultCustomComponentSettings(): ICustomComponent {
    return {
      id: UUIDHelper.create(),
      type: EComponentType.customComponent,
      name: 'Custom',
      css: {},
      h: 600,
      w: 600,
      x: 0,
      y: 0,
      // for customcode props
      customHtml: '',
      customCss: '',
      customJs: '',
    };
  }

  static get defaultWebcamSettings(): IWebcamComponent {
    return {
      id: UUIDHelper.create(),
      type: EComponentType.webcam,
      name: 'Webcam',
      css: {},
      h: 281,
      w: 500,
      x: 0,
      y: 0,
      lockAspectRatio: true,
      aspectRatio: 1.778,
      preview: true,
      facingMode: 'user',
      videoDeviceId: null,
      audioDeviceId: null,
      delay: 3,
      verticalSettings: {
        h: 405,
        w: 720,
        x: 0,
        y: 0,
        useFullWidth: true,
      },
    };
  }

  static get defaultStreamLabelSettings(): IStreamLabelComponent {
    return {
      id: UUIDHelper.create(),
      type: EComponentType.streamLabel,
      name: 'Stream Label',
      css: {
        fontSize: 32,
        color: '#ffffff',
      },
      h: 72,
      w: 400,
      x: 0,
      y: 0,
      value: '',
    };
  }

  static get defaultGameCaptureSettings(): IGameCaptureComponent {
    return {
      id: UUIDHelper.create(),
      type: EComponentType.gameCapture,
      name: 'Game Capture',
      css: {},
      h: 720,
      w: 1280,
      x: 0,
      y: 0,
      lockAspectRatio: true,
      aspectRatio: 1.778,
      fixedPosition: true,
      sortIndex: 1,
      hideable: false,
      verticalSettings: {
        x: 0,
        y: 0,
        w: 720,
        h: 405,
        useFullWidth: true,
      },
    };
  }

  static getComponentIcon(type: TComponentType): string {
    switch (type) {
      case EComponentType.text:
        return 'icon-text';
      case EComponentType.image:
        return 'icon-image';
      case EComponentType.alertBox:
        return 'icon-notifications';
      case EComponentType.video:
        return 'icon-media-share-2';
      case EComponentType.customComponent:
        return 'icon-editor-7';
      case EComponentType.webcam:
        return 'icon-studio';
      case EComponentType.chat:
        return 'icon-chat-box';
      case EComponentType.eventList:
        return 'icon-event-list';
      case EComponentType.streamLabel:
        return 'icon-stream-labels';
      case EComponentType.gameCapture:
        return 'icon-console';
      default:
        return 'icon-more';
    }
  };

  static getImageOriginalSizeAndAspectRatio(imageUrl: string, maxWidth: number = 1920): Promise<{ width: number, height: number, aspectRatio: number; }> {
    return new Promise((resolve, reject) => {
      const img = new Image();

      img.onload = () => {
        let width = img.naturalWidth;
        let height = img.naturalHeight;
        const maxHeight = maxWidth / 16 * 9;

        if (width > height && width > maxWidth) {
          const imageRatio = width / maxWidth;
          width = maxWidth;
          height = height / imageRatio;
        } else if (height > width && height > maxHeight) {
          const imageRatio = height / maxHeight;
          height = maxHeight;
          width = width / imageRatio;
        }

        resolve({
          width: Math.round(width),
          height: Math.round(height),
          aspectRatio: width / height,
        });
      };

      img.onerror = reject;

      img.src = imageUrl;
    });
  }

  static getComponentSettingsByType(type: TComponentType): IComponent {
    const settings = ComponentHelper[`default${startCase(camelCase(type)).replace(/ /g, '')}Settings`];

    if (settings.lockAspectRatio && settings.aspectRatio) {
      settings.verticalSettings = {
        ...settings.verticalSettings,
        lockAspectRatio: settings.lockAspectRatio,
        aspectRatio: settings.aspectRatio,
      };
    }

    return settings;
  }
}

export enum ECanvasSidebar {
  layer = 'layer',
  settings = 'settings',
}

export type TCanvasSidebar = ECanvasSidebar.layer | ECanvasSidebar.settings;

export interface IOnboarding {
  step: number;
  title: string;
  description: string;
  buttonText?: string;
  videoSrc?: string;
}

export interface IOnboardingSteps {
  editor: number;
  project: number;
}

export interface IMediaInputDevices {
  deviceId: string;
  kind: string;
  label: string;
  groupId: string;
}

export type TVideoMobileInputDevice = 'user' | 'environment';

export interface IComponentListGroup {
  simpleComponents: ISelector[];
  streamlabsComponents: ISelector[];
}

export const editorSettings = {
  maxUndoSteps: 10,
  maxRedoSteps: 5
};


export const StreamStatsFPSHealthBrackets = {
  '720@30': {
    full: 3,
    half: 2,
    quarter: 1.2
  },
  '720@60': {
    full: 5,
    half: 3,
    quarter: 1.8
  },
  '1080@30': {
    full: 5,
    half: 3,
    quarter: 1.8
  },
  '1080@60': {
    full: 6,
    half: 4,
    quarter: 3
  }
};

export enum EEditorType {
  horizontal = 'horizontal',
  vertical = 'vertical',
}

export type TEditorType = EEditorType.horizontal | EEditorType.vertical;
